// Écran Journal OD — Opérations Diverses : saisie libre multi-lignes.
// L'utilisateur saisit lui-même le sens via deux colonnes Débit / Crédit.
// Enregistrement impossible tant que Total Débit ≠ Total Crédit.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SelecteurCompte from "@/components/SelecteurCompte";
import DatePicker from "@/components/DatePicker";
import JournalEcritures from "@/components/JournalEcritures";
import ApercuEcritures from "@/components/ApercuEcritures";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, Shuffle, CheckCircle2, AlertTriangle, Copy } from "lucide-react";

const fmt = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} XPF`;
const fmtDateCourte = (iso) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};
const aujourdHui = () => new Date().toISOString().slice(0, 10);
const ligneVide = () => ({ compte: null, debit: "", credit: "", description: "" });

export default function OperationsDiverses() {
  const [dateOperation, setDateOperation] = useState(aujourdHui());
  const [libelle, setLibelle] = useState("");
  const [lignes, setLignes] = useState([ligneVide(), ligneVide()]);
  const [numero, setNumero] = useState("");
  const [enregistrement, setEnregistrement] = useState(false);
  const [resetSeq, setResetSeq] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  // Duplication d'opérations récurrentes
  const [dialogDup, setDialogDup] = useState(false);
  const [operations, setOperations] = useState([]);
  const [chargementOps, setChargementOps] = useState(false);
  const formRef = useRef(null);

  const charger = useCallback(async () => {
    try {
      const n = await api.get("/operations/prochain-numero", { params: { type: "od" } });
      setNumero(n.data?.numero || "");
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const ouvrirDuplication = async () => {
    setDialogDup(true);
    setChargementOps(true);
    try {
      const { data } = await api.get("/operations", { params: { type: "od" } });
      setOperations(data || []);
    } catch {
      setOperations([]);
    } finally {
      setChargementOps(false);
    }
  };

  const dupliquer = (op) => {
    setLibelle(op.libelle || "");
    setDateOperation(aujourdHui());
    setLignes((op.lignes || []).map((l) => ({
      compte: { numero: l.compte, libelle: l.libelle_compte || "" },
      debit: l.debit ? String(Math.round(l.debit)) : "",
      credit: l.credit ? String(Math.round(l.credit)) : "",
      description: l.description || "",
    })));
    setResetSeq((s) => s + 1);
    setDialogDup(false);
    toast.success(`Opération ${op.numero} dupliquée. Vérifiez et enregistrez.`);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  // Saisir un débit efface le crédit de la ligne (et inversement).
  const majLigne = (i, champ, valeur) =>
    setLignes((ls) => ls.map((l, idx) => {
      if (idx !== i) return l;
      if (champ === "debit") return { ...l, debit: valeur, credit: "" };
      if (champ === "credit") return { ...l, credit: valeur, debit: "" };
      return { ...l, [champ]: valeur };
    }));
  const ajouterLigne = () => setLignes((ls) => [...ls, ligneVide()]);
  const supprimerLigne = (i) => setLignes((ls) => (ls.length > 2 ? ls.filter((_, idx) => idx !== i) : ls));

  const totalDebit = lignes.reduce((s, l) => s + Math.round(parseFloat(l.debit) || 0), 0);
  const totalCredit = lignes.reduce((s, l) => s + Math.round(parseFloat(l.credit) || 0), 0);
  const equilibre = totalDebit === totalCredit && totalDebit > 0;

  const apercuLignes = lignes
    .filter((l) => l.compte && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0))
    .map((l) => ({
      compte: l.compte.numero, libelle: l.compte.libelle || l.description,
      debit: Math.round(parseFloat(l.debit) || 0), credit: Math.round(parseFloat(l.credit) || 0),
    }));

  const reinitialiser = () => {
    setDateOperation(aujourdHui());
    setLibelle("");
    setLignes([ligneVide(), ligneVide()]);
    setResetSeq((s) => s + 1);
  };

  const enregistrer = async () => {
    if (!libelle.trim()) { toast.error("Veuillez saisir un libellé."); return; }
    const lignesValides = lignes.filter((l) => l.compte && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0));
    if (lignesValides.length < 2) { toast.error("Renseignez au moins deux lignes complètes."); return; }
    if (!equilibre) { return; }

    setEnregistrement(true);
    try {
      await api.post("/operations/od", {
        date_operation: dateOperation,
        libelle,
        lignes: lignesValides.map((l) => {
          const debit = Math.round(parseFloat(l.debit) || 0);
          return {
            compte: l.compte.numero, libelle_compte: l.compte.libelle,
            sens: debit > 0 ? "+" : "-",
            montant: debit > 0 ? debit : Math.round(parseFloat(l.credit) || 0),
            description: l.description,
          };
        }),
      });
      toast.success("Opération enregistrée avec succès.");
      reinitialiser();
      charger();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="fenua-fade-up" data-testid="page-operations-diverses">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <Shuffle className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">OD — Opérations Diverses</h1>
            <p className="text-sm text-[#1D3D3D]/70">Saisie libre : indiquez un Débit ou un Crédit par ligne. Total Débit = Total Crédit.</p>
          </div>
        </div>
        <Button type="button" variant="secondary" data-testid="bouton-dupliquer-od" onClick={ouvrirDuplication}
          className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
          <Copy className="h-4 w-4 mr-1" /> Dupliquer une opération
        </Button>
      </div>

      <div className="mt-6">
        <JournalEcritures code="OD" libelleJournal="OD — Opérations Diverses" refreshKey={refreshKey} />
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6" ref={formRef}>
        <div className="xl:col-span-2 bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Libellé de l'opération</Label>
              <Input data-testid="od-libelle" value={libelle} onChange={(e) => setLibelle(e.target.value)}
                placeholder="Ex. Régularisation, dotation…"
                className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Date</Label>
              <DatePicker testId="od-date" value={dateOperation} onChange={setDateOperation} />
            </div>
          </div>
          <div className="text-xs text-[#1D3D3D]/60 font-mono">{numero}</div>

          <div className="space-y-3">
            <div className="hidden sm:grid grid-cols-12 gap-2 text-xs uppercase tracking-wide text-[#4A8A8A] font-semibold px-1">
              <div className="col-span-5">Compte</div>
              <div className="col-span-3 text-right">Débit</div>
              <div className="col-span-3 text-right">Crédit</div>
              <div className="col-span-1" />
            </div>
            {lignes.map((l, i) => (
              <div key={`${resetSeq}-${i}`} data-testid={`od-ligne-${i}`} className="rounded-lg border border-[#4A8A8A]/20 p-3 space-y-3 bg-[#E8F4F4]/30">
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center">
                  <div className="sm:col-span-5">
                    <SelecteurCompte testId={`od-compte-${i}`} valeurInitiale={l.compte}
                      onSelect={(c) => majLigne(i, "compte", c)} placeholder="Compte (PCG)" />
                  </div>
                  <div className="sm:col-span-3">
                    <Input type="number" step="1" min="0" inputMode="numeric" data-testid={`od-debit-${i}`}
                      value={l.debit} onChange={(e) => majLigne(i, "debit", e.target.value)} placeholder="Débit"
                      className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A] text-right" />
                  </div>
                  <div className="sm:col-span-3">
                    <Input type="number" step="1" min="0" inputMode="numeric" data-testid={`od-credit-${i}`}
                      value={l.credit} onChange={(e) => majLigne(i, "credit", e.target.value)} placeholder="Crédit"
                      className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A] text-right" />
                  </div>
                  <div className="sm:col-span-1 flex justify-end">
                    <button type="button" data-testid={`od-supprimer-${i}`} onClick={() => supprimerLigne(i)}
                      className="h-10 w-10 rounded-md flex items-center justify-center text-red-500 hover:bg-red-50 disabled:opacity-30"
                      disabled={lignes.length === 2}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <Input data-testid={`od-description-${i}`} value={l.description}
                  onChange={(e) => majLigne(i, "description", e.target.value)} placeholder="Commentaire (facultatif)"
                  className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A] text-sm" />
              </div>
            ))}
            <Button type="button" variant="secondary" data-testid="od-ajouter-ligne" onClick={ajouterLigne}
              className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
              <Plus className="h-4 w-4 mr-1" /> Ajouter une ligne
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-[#1D3D3D] mb-4">Équilibre</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#1D3D3D]/70">Total Débit</span><span data-testid="od-total-debit" className="font-medium text-[#1D3D3D]">{fmt(totalDebit)}</span></div>
              <div className="flex justify-between"><span className="text-[#1D3D3D]/70">Total Crédit</span><span data-testid="od-total-credit" className="font-medium text-[#1D3D3D]">{fmt(totalCredit)}</span></div>
            </div>
            <div data-testid="od-equilibre" className={`mt-3 flex items-center gap-2 text-sm font-medium ${equilibre ? "text-emerald-600" : "text-amber-700"}`}>
              {equilibre ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {equilibre ? "Écriture équilibrée (Débit = Crédit)" : "Total Débit doit être égal au Total Crédit"}
            </div>
            <Button data-testid="bouton-enregistrer-od" onClick={enregistrer} disabled={enregistrement || !equilibre}
              className="w-full mt-4 bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold h-11">
              {enregistrement ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer l'opération"}
            </Button>
          </div>

          <ApercuEcritures lignes={apercuLignes} />
        </div>
      </div>

      <Dialog open={dialogDup} onOpenChange={setDialogDup}>
        <DialogContent data-testid="dialog-dupliquer" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dupliquer une opération</DialogTitle>
            <DialogDescription>
              Sélectionnez une opération existante : ses lignes seront recopiées dans le formulaire (date du jour). Idéal pour les saisies récurrentes.
            </DialogDescription>
          </DialogHeader>
          {chargementOps ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
          ) : operations.length === 0 ? (
            <p data-testid="dup-vide" className="py-6 text-center text-sm text-[#1D3D3D]/60">Aucune opération diverse à dupliquer pour l'instant.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-[#4A8A8A]/10" data-testid="dup-liste">
              {operations.map((op) => (
                <button key={op.id} type="button" data-testid={`dup-op-${op.numero}`} onClick={() => dupliquer(op)}
                  className="w-full text-left px-3 py-3 hover:bg-[#E8F4F4]/60 transition-colors flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1D3D3D] truncate">{op.libelle}</p>
                    <p className="text-xs text-[#4A8A8A] font-mono">{op.numero} · {fmtDateCourte(op.date_operation)} · {(op.lignes || []).length} lignes</p>
                  </div>
                  <span className="text-sm font-medium text-[#1D3D3D] whitespace-nowrap">{fmt(op.total_debit)}</span>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
